import {
  BitkitButton,
  BitkitControlButton,
  BitkitLink,
  BitkitTooltip,
  createBitkitToast,
  IconBlockCircle,
} from '@bitrise/bitkit-v2';
import { useDisclosure } from '@chakra-ui/react/hooks';
import { AxiosError } from 'axios';
import pick from 'lodash/pick';
import { useEffect, useMemo, useState } from 'react';

import useTestQuarantineAdd from '@/client/BFF/App/TestQuarantine/useTestQuarantineAdd';
import useTestQuarantineItem from '@/client/BFF/App/TestQuarantine/useTestQuarantineItem';
import useTestQuarantineList from '@/client/BFF/App/TestQuarantine/useTestQuarantineList';
import TestQuarantineItemDialog from '@/pages/App/TestQuarantinePage/components/TestQuarantineItemDialog';
import { QuarantineItem } from '@/pages/App/TestQuarantinePage/TestQuarantinePage.types';
import { projectTestQuarantinePage } from '@/utils/monolithApiRouteService';

export type AddToQuarantineButtonProps = {
  buildNumber?: number;
  buildSlug?: string;
  canAccessTestQuarantine: boolean;
  className?: string;
  projectSlug: string;
  source: QuarantineItem['quarantinedFrom']['source'];
  testCaseName?: string;
  testCaseIdentifier?: string;
  testSuiteName?: string[];
  workflowName?: string;
};

const AddToQuarantineButton = (props: AddToQuarantineButtonProps) => {
  const {
    buildNumber,
    buildSlug,
    canAccessTestQuarantine,
    className,
    projectSlug,
    source,
    testCaseName,
    testCaseIdentifier,
    testSuiteName,
    workflowName,
  } = props;

  const [isDisabled, setIsDisabled] = useState(!canAccessTestQuarantine);
  const [isLoading, setIsLoading] = useState(canAccessTestQuarantine);

  const defaultQuarantineItem: QuarantineItem = useMemo(
    () => ({
      testCaseName: testCaseName || '',
      testCaseIdentifier: testCaseIdentifier,
      testSuiteName: testSuiteName || [],
      className: className || '',
      filters: {
        branches: [],
        targets: workflowName ? [workflowName] : [],
      },
      id: '',
      quarantinedFrom: {
        number: undefined,
        source: 'build',
        slug: undefined,
      },
      quarantinedBy: '',
      quarantinedAt: '',
      comment: '',
    }),
    [className, testCaseName, testCaseIdentifier, testSuiteName, workflowName],
  );

  const [selectedQuarantineItem, setSelectedQuarantineItem] = useState<QuarantineItem>(defaultQuarantineItem);

  const { open, onOpen, onClose } = useDisclosure();

  const handleError = (err: AxiosError) => {
    const errorData = err.response?.data as { errorMsg?: string } | undefined;
    createBitkitToast({
      messageText: errorData?.errorMsg || 'Please try again or contact support.',
      titleText: 'Something went wrong',
      variant: 'critical',
    });
  };

  const { data: listData, isPending: isListPending } = useTestQuarantineList(projectSlug, {
    enabled: canAccessTestQuarantine,
  });

  const { isPending: isAddPending, mutate: addToQuarantine } = useTestQuarantineAdd(projectSlug, {
    onSuccess: (response) => {
      onClose();
      setSelectedQuarantineItem(response.data);
      createBitkitToast({
        messageText: (
          <>
            {testCaseName} test case will not run in the defined scope until you remove it from{' '}
            <BitkitLink href={projectTestQuarantinePage(projectSlug)} isExternal textDecoration="underline">
              quarantine
            </BitkitLink>
            .
          </>
        ),
        titleText: 'Quarantined',
        variant: 'success',
      });
    },
    onError: handleError,
  });

  const { isPending: isEditPending, mutate: editQuarantineItem } = useTestQuarantineItem<QuarantineItem>(
    projectSlug,
    'patch',
    {
      onSuccess: (response) => {
        onClose();
        setSelectedQuarantineItem(response.data);
      },
      onError: handleError,
    },
  );

  const onSubmit = (data: QuarantineItem) => {
    if (data.id) {
      editQuarantineItem({ id: data.id, payload: data });
    } else {
      addToQuarantine({
        ...pick(data, ['className', 'testCaseName', 'testCaseIdentifier', 'testSuiteName', 'filters', 'comment']),
        quarantinedFrom: {
          source: source,
          number: buildNumber,
          slug: buildSlug,
        },
        quarantinedBy: '',
        quarantinedAt: '',
        id: '',
      });
    }
  };

  useEffect(() => {
    if (!isListPending) {
      if (listData?.testCases) {
        // A test case reported by a display name is not identified by its name and class name, so
        // two of them can look alike here. An identifier match wins wherever it sits in the list,
        // and the name comparison only decides a pair where one side has no identifier.
        const quarantinedByIdentifier = testCaseIdentifier
          ? listData.testCases.find((item) => item.testCaseIdentifier === testCaseIdentifier)
          : undefined;

        const sameItemInQuarantine =
          quarantinedByIdentifier ||
          listData.testCases.find(
            (item) =>
              !(item.testCaseIdentifier && testCaseIdentifier) &&
              item.testCaseName === testCaseName &&
              JSON.stringify(item.testSuiteName) === JSON.stringify(testSuiteName) &&
              item.className === className,
          );
        setSelectedQuarantineItem(sameItemInQuarantine || defaultQuarantineItem);
      }
      setIsLoading(false);
      setIsDisabled(!listData?.canChange);
    }
  }, [
    className,
    defaultQuarantineItem,
    isListPending,
    listData,
    testCaseIdentifier,
    testCaseName,
    testSuiteName,
    workflowName,
  ]);

  const buttonLabel = selectedQuarantineItem.id ? 'Edit quarantine scope' : 'Set up quarantine';
  let tooltipLabel =
    source !== 'insights'
      ? 'Quarantined test cases will be excluded from test runs preventing unexpected builds failures.'
      : 'Configure where to exclude this test from future runs.';

  if (source === 'insights' && selectedQuarantineItem.id) {
    tooltipLabel = 'Test case is already in quarantine.';
  }

  if (!canAccessTestQuarantine) {
    tooltipLabel += ' To be able to use quarantine features you need an Enterprise plan.';
  } else if (!listData?.canChange) {
    tooltipLabel = `You can't modify the quarantine list.`;
  }

  return (
    <>
      <BitkitTooltip text={tooltipLabel}>
        {source !== 'insights' ? (
          <BitkitButton
            state={isLoading ? 'loading' : isDisabled ? 'disabled' : undefined}
            icon={IconBlockCircle}
            marginInlineStart="auto"
            onClick={onOpen}
            size="sm"
            variant="secondary"
          >
            {buttonLabel}
          </BitkitButton>
        ) : (
          <BitkitControlButton
            label={buttonLabel}
            icon={IconBlockCircle}
            state={isLoading ? 'skeleton' : isDisabled || !!selectedQuarantineItem.id ? 'disabled' : undefined}
            tooltipProps={{ disabled: true }}
            onClick={onOpen}
            size="sm"
          />
        )}
      </BitkitTooltip>
      {open && (
        <TestQuarantineItemDialog
          isLoading={isAddPending || isEditPending}
          onCancel={onClose}
          onSubmit={onSubmit}
          testQuarantineItem={selectedQuarantineItem}
        />
      )}
    </>
  );
};

export default AddToQuarantineButton;
